import { defineEnvVariable } from '@nzyme/ioc/defineEnvVariable.js';
import { DOMAIN_TOP_LEVEL } from './domains.js';
import { ENV, LOCAL, LOCALSTACK, PRODUCTION, USERNAME, WORKTREE_NAME } from './environments.js';
/**
 * The AWS region the current server process operates in, injected by the environment and never
 * hardcoded in application code:
 * - deployed: AWS Lambda injects it automatically; the EC2 actors cluster injects its own stack's
 *   region into the process env (see the infra `ApiClusterStack`);
 * - local/dev/test: `setupAwsEnv()` sets it from {@link AWS_REGION_LOCAL}.
 *
 * Required (no fallback): a deployed process with no region is a misconfiguration that must fail
 * loudly at boot via `AWS_REGION.assert()` rather than silently stall on the first AWS SDK call
 * (the SIG-208 failure mode, where a bare `Region is missing` was swallowed).
 *
 * Not for browser/SSR code — that has no runtime `process.env`. The browser Cognito client takes its
 * region from the user pool it was configured with, via `getUserPoolRegion()`.
 */
export const AWS_REGION = defineEnvVariable('AWS_REGION');
/**
 * Region holding the infrastructure's own state: the Pulumi DIY S3 backend and the KMS key backing
 * the secrets provider. This is about *where infra lives*, not where any workload runs, so it stays
 * a fixed constant independent of the deployment region(s). Used only by the Pulumi tooling.
 *
 * `us-east-1` is the Healed organization's home region (D2) — the state of every new account is
 * created there by `sigma infra bootstrap`, and Pulumi reads it back from the same place.
 */
export const AWS_REGION_INFRA_STATE = 'us-east-1';
/**
 * The single region used when deploying the (identical) Pulumi infrastructure for the `local` and
 * `dev` environments, and the region the local/dev/test runtime points its AWS SDK clients at
 * (via {@link setupAwsEnv}). Deployed environments do NOT use this — their runtimes get the region
 * injected by infrastructure into `process.env.AWS_REGION` (Lambda automatically; the EC2 cluster
 * explicitly). Read it through {@link AWS_REGION} in server code, never as a hardcoded default.
 */
export const AWS_REGION_LOCAL = 'us-east-1';
export const AWS_ENDPOINT_URL = getAwsEndpointUrl();
export const PROJECT_PREFIX = 'healed';
export const PROJECT_NAME = `${PROJECT_PREFIX}-${ENV}`;
/**
 * Name of the S3 bucket holding this environment's Pulumi state, in {@link AWS_REGION_INFRA_STATE}.
 * Undefined under LocalStack, whose backend is a directory on disk instead.
 *
 * Pulumi cannot create this bucket — it is where Pulumi would record having created it — so it is
 * provisioned out of band by `sigma infra bootstrap`. That command and the Pulumi backend have to
 * name the same bucket byte for byte, which is why the name is built here once rather than on both
 * sides.
 *
 * `dev` gets a per-developer bucket: every developer deploys the same stacks into their own AWS
 * account, so one shared name would collide between them.
 */
export const AWS_BUCKET_INFRA_STATE = LOCALSTACK
    ? undefined
    : `${PROJECT_PREFIX}-infra-${ENV}${LOCAL ? `-${USERNAME}` : ''}`;
/**
 * KMS alias of the key the Pulumi secrets provider encrypts stack secrets with — one key per AWS
 * account, in {@link AWS_REGION_INFRA_STATE}. Unused by `local` and `dev`, which encrypt their state
 * with a passphrase instead; `getPulumiConfig` owns that split.
 */
export const AWS_KMS_ALIAS_INFRA_SECRETS = 'alias/pulumi-secret';
export { DOMAIN_TOP_LEVEL } from './domains.js';
export const DOMAIN = PRODUCTION
    ? DOMAIN_TOP_LEVEL
    : ENV === 'local'
        ? `${WORKTREE_NAME}.localhost`
        : ENV === 'dev'
            ? `sigma.localhost`
            : `${ENV}.${DOMAIN_TOP_LEVEL}`;
export const DEFAULT_COUNTRY = 'US';
/**
 * Nameservers of each subordinate zone, so the production zone can delegate to it. Hardcoded
 * rather than looked up, to avoid sharing permissions between the prod and testing AWS accounts —
 * each value is read from that environment's `dns` stack output (`nsRecords`) once its zone
 * exists.
 *
 * `dev` has no entry and never will: the dev tunnel runs on Tailscale, which addresses machines
 * over MagicDNS and needs no zone of ours (`docs/decisions/tailscale-dev-tunnel.md`).
 *
 * TODO(SIG-384): add `demo` once its zone is stood up.
 */
export const NAMESERVERS = {
    testing: ['ns-1214.awsdns-23.org', 'ns-1615.awsdns-09.co.uk', 'ns-468.awsdns-58.com', 'ns-996.awsdns-60.net'],
};
export const COGNITO_ID_CLAIM_NAME = 'id';
export const COGNITO_ID_CLAIM = `custom:${COGNITO_ID_CLAIM_NAME}`;
export const COGNITO_LOCALE_CLAIM = 'locale';
export const COGNITO_ROLE_CLAIM_NAME = 'role';
export const COGNITO_ROLE_CLAIM = `custom:${COGNITO_ROLE_CLAIM_NAME}`;
function getAwsEndpointUrl() {
    if (ENV === 'local') {
        return 'http://localhost.localstack.cloud:4566';
    }
    return undefined;
}
//# sourceMappingURL=resources.js.map